import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Ban, ChevronRight, FileCheck, FileX, MonitorOff, MonitorUp, Plus, RefreshCw, Search, SquareX, X } from 'lucide-react';
import { apiClient } from '../api';
import orgApi from '../api/organizations';
import { useAuth } from '../auth/AuthProvider';
import { errMessage, primaryBtn, primaryBtnStyle, secondaryBtnStyle } from '../components/UI/ink';
import NewActionDrawer from '../components/ResponseActions/NewActionDrawer';
import {
  STATUS, TYPE_LABEL, UNDO_LABEL, agentLine, ago, agoLong, caseRef, cfgOf, explain, failureHint, isPlaybook,
  minutesSince, resultOf, runRef, stamp, titleOf,
} from '../components/ResponseActions/model';

// Every response action, where it is in its lifecycle, and what is still in
// effect. The status strip filters; a row opens the docked pane (?action=),
// which explains exactly what the agent runs and holds the next step.
const GIVE_UP_MIN = 10; // matches the Worker: after this a dispatched action can be abandoned
const selectStyle = { background: 'var(--surf-surface)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' };
const mono = 'font-mono text-[10.5px] tracking-[0.12em]';
const ICON = { block_ip: Ban, unblock_ip: Ban, block_domain: Ban, unblock_domain: Ban, isolate_host: MonitorOff, release_isolation: MonitorUp, kill_process: SquareX, quarantine_file: FileX, restore_file: FileCheck };
const STRIP = [
  ['pending', 'WAITING FOR CONFIRMATION'],
  ['sent', 'SENT TO AGENT'],
  ['completed', 'DONE'],
  ['failed', 'FAILED'],
];
const inStrip = (f, a) => (f === 'sent' ? a.status === 'confirmed' || a.status === 'dispatched' : a.status === f);

const effectText = (a) => {
  const c = cfgOf(a);
  if (a.action_type === 'block_ip') return `Traffic to and from ${c.ip} blocked`;
  if (a.action_type === 'block_domain') return `${c.domain} blocked`;
  if (a.action_type === 'quarantine_file') return `${String(c.path || 'File').split(/[\\/]/).pop()} held in quarantine`;
  return 'Isolated: only DNS and DragonEye reachable';
};

const Marker = ({ st, size = 8 }) => (
  <span className="shrink-0 box-border rounded-sm border" style={{ width: size, height: size, background: STATUS[st]?.mk, borderColor: STATUS[st]?.mkB === 'transparent' ? STATUS[st]?.mk : STATUS[st]?.mkB }} />
);

export default function ResponseActions() {
  const { user, permissions = [] } = useAuth();
  const me = user?.email || user?.sub || '';
  const canConfirm = permissions.includes('actions:confirm');
  const canCreate = permissions.includes('actions:create');
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sf, setSf] = useState('');
  const [src, setSrc] = useState('');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [members, setMembers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [paneError, setPaneError] = useState(null);

  const selectedId = searchParams.get('action');
  const select = useCallback((id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('action', id); else next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  useEffect(() => { setCancelling(false); setCancelReason(''); setTyped(''); setPaneError(null); }, [selectedId]);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const r = await apiClient.get('/api/actions?limit=300');
      setData(r.data); setError(null);
    } catch (err) {
      if (!quiet) setError(errMessage(err, 'Could not load response actions.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { orgApi.getMembers().then(m => setMembers(m?.members || m?.users || [])).catch(() => {}); }, []);

  const actions = useMemo(() => data?.actions || [], [data]);
  // While something is with an agent, refresh quietly so progress shows up.
  const moving = actions.some(a => a.status === 'confirmed' || a.status === 'dispatched');
  useEffect(() => {
    if (!moving) return undefined;
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, [moving, load]);

  const nameOf = useCallback((email) => {
    if (!email) return '';
    if (email === 'system') return 'DragonEye';
    const m = members.find(x => x.email?.toLowerCase() === String(email).toLowerCase());
    return m?.full_name || m?.name || email;
  }, [members]);
  const byOf = (a) => (isPlaybook(a) ? `${a.workflow_name || 'Playbook'} · ${runRef(a.triggered_by)}` : nameOf(a.requested_by));

  const counts = data?.counts || {};
  const n = (k) => (k === 'sent' ? (counts.confirmed || 0) + (counts.dispatched || 0) : counts[k] || 0);
  const fromRuns = actions.filter(a => a.status === 'pending' && isPlaybook(a)).length;
  const stripSub = { pending: fromRuns ? `${fromRuns} from playbooks` : 'nothing runs until then', sent: 'picked up within 30 s', completed: `${counts.completed_this_week || 0} this week`, failed: 'needs a look' };

  const needle = q.trim().toLowerCase();
  const list = actions.filter(a => {
    if (src === 'cancelled') { if (a.status !== 'cancelled') return false; } else if (sf && !inStrip(sf, a)) return false;
    if (src === 'run' && !isPlaybook(a)) return false;
    if (src === 'people' && isPlaybook(a)) return false;
    if (type && a.action_type !== type && a.action_type !== { block_ip: 'unblock_ip', block_domain: 'unblock_domain', quarantine_file: 'restore_file', isolate_host: 'release_isolation' }[type]) return false;
    if (needle) {
      const hay = [titleOf(a), a.hostname, a.host_ip, a.action_config, a.case_id, a.case_title, a.reason, a.requested_by, nameOf(a.requested_by)].join(' ').toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
  const listLabel = `${list.length} ${src === 'cancelled' ? 'CANCELLED' : sf ? STRIP.find(s => s[0] === sf)[1] : 'ACTIONS'}`;

  const x = actions.find(a => a.id === selectedId) || null;
  const effects = data?.in_effect || [];
  const pendingN = counts.pending || 0;
  const summary = !data ? 'Loading…' : [
    pendingN ? `${pendingN} waiting for confirmation` : 'Nothing waiting for confirmation',
    `${n('sent')} with an agent`,
    `${effects.length} still in effect`,
  ].join(' · ');

  // ── mutations ──
  const run = async (fn, fallback) => {
    setBusy(true); setPaneError(null);
    try {
      const r = await fn();
      await load(true);
      return r;
    } catch (err) {
      setPaneError(errMessage(err, fallback));
      await load(true);
      return null;
    } finally {
      setBusy(false);
    }
  };
  const confirm = () => run(() => apiClient.post(`/api/actions/${x.id}/confirm`, x.action_type === 'isolate_host' ? { hostname: typed.trim() } : {}), 'Could not confirm the action.');
  const doCancel = async () => {
    const r = await run(() => apiClient.post(`/api/actions/${x.id}/cancel`, { reason: cancelReason.trim() }), 'Could not cancel the action.');
    if (r) { setCancelling(false); setCancelReason(''); }
  };
  const stopWaiting = () => run(() => apiClient.post(`/api/actions/${x.id}/cancel`, {}), 'Could not stop waiting.');
  const spawn = async (path, fallback) => {
    const r = await run(() => apiClient.post(path, {}), fallback);
    if (r?.data?.action?.id) select(r.data.action.id);
  };
  const undo = (a) => spawn(`/api/actions/${a.id}/undo`, 'Could not queue the undo.');
  const retry = () => spawn(`/api/actions/${x.id}/retry`, 'Could not queue it again.');

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-4" style={{ color: 'var(--ink-text)' }}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[11.5px] tracking-[0.14em]" style={{ color: 'var(--ink-link)' }}>INTELLIGENCE</span>
          <h1 className="m-0 font-heading text-[30px] font-semibold tracking-[-0.01em] leading-tight">Response Actions</h1>
          <p className="m-0 text-sm" style={{ color: 'var(--ink-muted)' }}>{summary}</p>
        </div>
        <div className="flex items-center gap-2.5">
          <button type="button" onClick={() => load()} disabled={loading} aria-label="Refresh" className="w-11 h-11 flex items-center justify-center rounded-[10px] border disabled:opacity-60" style={secondaryBtnStyle}><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.8} /></button>
          {canCreate && <button type="button" onClick={() => setCreating(true)} className={primaryBtn} style={primaryBtnStyle}><Plus className="w-4 h-4" strokeWidth={2} />New action</button>}
        </div>
      </div>

      {error && <div role="alert" className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-[10px] border text-[13px]" style={{ background: 'var(--sev-crit-bg)', borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)' }}><span>{error}</span><button type="button" onClick={() => setError(null)} aria-label="Dismiss"><X className="w-3.5 h-3.5" /></button></div>}

      {/* Lifecycle strip, which doubles as the filter */}
      <div role="group" aria-label="Action status" className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(170px,1fr))]">
        {STRIP.map(([id, k], i) => {
          const on = sf === id && !src;
          const st = id === 'sent' ? 'confirmed' : id;
          return (
            <button key={id} type="button" onClick={() => { setSf(on ? '' : id); setSrc(''); }} aria-pressed={on} className="h-[70px] box-border flex flex-col justify-center gap-1 px-4 rounded-xl border text-left" style={{ background: on ? 'var(--surf-raised)' : 'var(--surf-surface)', borderColor: on ? 'var(--accent)' : 'var(--surf-line)' }}>
              <span className="flex items-center justify-between"><span className={`flex items-center gap-[7px] ${mono}`} style={{ color: 'var(--ink-faint)' }}><Marker st={st} size={7} />{k}</span>{i < 2 && <ChevronRight className="w-3.5 h-3.5 hidden md:block" strokeWidth={2} style={{ color: 'var(--surf-line-strong)' }} />}</span>
              <span className="flex items-baseline gap-2"><span className="font-heading text-2xl font-semibold leading-none">{data ? n(id) : '—'}</span><span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{stripSub[id]}</span></span>
            </button>
          );
        })}
      </div>

      {/* Containment still in place, each with its undo */}
      {effects.length > 0 && (
        <section aria-label="In effect now" className="rounded-[14px] border overflow-hidden" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          <div className="h-[42px] box-border flex items-center px-4 border-b" style={{ borderColor: 'var(--surf-line)' }}>
            <span className={mono} style={{ color: 'var(--ink-faint)' }}>IN EFFECT NOW · {effects.length}</span>
            <span className="ml-auto text-xs hidden sm:inline" style={{ color: 'var(--ink-faint)' }}>Containment still active on a host</span>
          </div>
          {effects.map(e => (
            <div key={e.id} className="flex flex-wrap items-center gap-3 min-h-[52px] box-border py-2 pl-4 pr-3 border-b last:border-b-0" style={{ borderColor: 'var(--surf-line)' }}>
              <button type="button" onClick={() => select(e.id)} className="font-mono text-[12.5px] min-w-[110px] text-left hover:underline">{e.hostname}</button>
              <span className="flex-[1_1_260px] text-[13px]" style={{ color: 'var(--ink-muted)' }}>{effectText(e)}</span>
              <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>since {stamp(e.completed_at)}</span>
              {e.undo_id ? (
                <button type="button" onClick={() => select(e.undo_id)} className="h-[34px] px-3 rounded-lg text-[12.5px]" style={{ color: 'var(--ink-link)' }}>{UNDO_LABEL[e.action_type]} {e.undo_status === 'pending' ? 'waiting for confirmation' : 'sent'}</button>
              ) : canCreate && (
                <button type="button" disabled={busy} onClick={() => undo(e)} className="h-[34px] px-3 rounded-lg border text-[12.5px] disabled:opacity-50" style={secondaryBtnStyle}>{UNDO_LABEL[e.action_type]}</button>
              )}
            </div>
          ))}
        </section>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-[0_1_280px] min-w-[180px] h-10 box-border flex items-center gap-2 px-3 rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          <Search className="w-[15px] h-[15px] shrink-0" strokeWidth={1.8} style={{ color: 'var(--ink-faint)' }} />
          <input type="search" aria-label="Search actions" placeholder="Host, IP, PID, file or case" value={q} onChange={e => setQ(e.target.value)} className="flex-grow min-w-0 h-[38px] bg-transparent border-0 outline-none text-[13.5px]" style={{ color: 'var(--ink-text)' }} />
        </div>
        {[['run', 'From playbooks', actions.filter(isPlaybook).length], ['people', 'From people', actions.filter(a => !isPlaybook(a)).length], ['cancelled', 'Cancelled', counts.cancelled || 0]].map(([id, t, c]) => {
          const on = src === id;
          return (
            <button key={id} type="button" onClick={() => { setSrc(on ? '' : id); if (id === 'cancelled') setSf(''); }} aria-pressed={on} className="h-10 flex items-center gap-1.5 px-3 rounded-[10px] border text-[13px]" style={{ background: on ? 'var(--surf-raised)' : 'var(--surf-surface)', borderColor: on ? 'var(--accent)' : 'var(--surf-line)' }}>
              {t}<span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{data ? c : '—'}</span>
            </button>
          );
        })}
        <div className="md:ml-auto">
          <label htmlFor="f-type" className="sr-only">Action type</label>
          <select id="f-type" value={type} onChange={e => setType(e.target.value)} className="h-10 box-border px-2.5 rounded-[10px] border text-[13px]" style={selectStyle}>
            <option value="">All actions</option><option value="block_ip">Block IP</option><option value="block_domain">Block domain</option><option value="kill_process">Stop process</option><option value="quarantine_file">Quarantine file</option><option value="isolate_host">Isolate host</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_440px] gap-5 items-start">
        <section aria-label="Actions" className="rounded-[14px] border overflow-hidden min-w-0" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          <div className="h-[42px] box-border flex items-center px-4 border-b" style={{ borderColor: 'var(--surf-line)' }}>
            <span className={mono} style={{ color: 'var(--ink-faint)' }}>{listLabel}</span>
            <span className="ml-auto text-xs" style={{ color: 'var(--ink-faint)' }}>Newest first</span>
          </div>
          {!list.length && (
            <div className="py-12 px-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
              {loading ? 'Loading…' : actions.length ? 'No actions match these filters.' : 'No response actions yet. Playbooks queue them here, or start one with New action.'}
            </div>
          )}
          {list.map(a => {
            const on = a.id === selectedId;
            const Icon = ICON[a.action_type] || Ban;
            const danger = a.action_type === 'isolate_host' && a.status === 'pending';
            return (
              <div key={a.id} className="relative">
                <button type="button" onClick={() => select(on ? null : a.id)} aria-pressed={on} className="w-full min-h-[68px] box-border flex items-center gap-3.5 py-2.5 px-4 border-b text-left" style={{ background: on ? 'var(--surf-raised)' : 'transparent', borderColor: 'var(--surf-line)' }}>
                  <span className="w-[34px] h-[34px] shrink-0 flex items-center justify-center rounded-[9px]" style={{ background: 'var(--surf-raised)', color: danger ? 'var(--sev-crit-fg)' : 'var(--ink-muted)' }}><Icon className="w-4 h-4" strokeWidth={1.9} /></span>
                  <span className="flex-grow min-w-0 flex flex-col gap-[5px]">
                    <span className="text-sm font-medium truncate">{titleOf(a)}</span>
                    <span className="flex items-center gap-2 min-w-0 font-mono text-[11px] whitespace-nowrap overflow-hidden" style={{ color: 'var(--ink-faint)' }}>
                      {isPlaybook(a) && <span className="h-[17px] shrink-0 inline-flex items-center px-[5px] border rounded text-[9.5px] tracking-[0.06em]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>PLAYBOOK</span>}
                      {a.reverses_id && <span className="h-[17px] shrink-0 inline-flex items-center px-[5px] border rounded text-[9.5px] tracking-[0.06em]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>UNDO</span>}
                      <span className="truncate">{byOf(a)}{a.case_id ? ` · ${caseRef(a.case_id)}` : ''}</span>
                    </span>
                  </span>
                  <span className="w-[176px] shrink-0 hidden sm:flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--ink-muted)' }}><Marker st={a.status} />{STATUS[a.status]?.l || a.status}</span>
                  <span className="w-11 shrink-0 text-right font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{ago(a.created_at)}</span>
                </button>
                {on && <span aria-hidden="true" className="absolute right-0 top-[22px] w-px h-6" style={{ background: 'var(--accent)' }} />}
              </div>
            );
          })}
        </section>

        <ActionPane
          x={x} me={me} nameOf={nameOf} byOf={byOf} othersCanConfirm={!!data?.others_can_confirm}
          canConfirm={canConfirm} canCreate={canCreate} busy={busy} error={paneError}
          cancelling={cancelling} setCancelling={setCancelling} cancelReason={cancelReason} setCancelReason={setCancelReason}
          typed={typed} setTyped={setTyped} onConfirm={confirm} onCancel={doCancel} onStopWaiting={stopWaiting}
          onUndo={() => undo(x)} onRetry={retry} onSelect={select}
        />
      </div>

      <NewActionDrawer
        open={creating}
        me={nameOf(me)}
        onClose={() => setCreating(false)}
        onCreated={(a, existing) => {
          if (!existing) setCreating(false);
          load(true);
          if (a?.id) select(a.id);
        }}
      />
    </div>
  );
}

function ActionPane({
  x, me, nameOf, byOf, othersCanConfirm, canConfirm, canCreate, busy, error, cancelling, setCancelling,
  cancelReason, setCancelReason, typed, setTyped, onConfirm, onCancel, onStopWaiting, onUndo, onRetry, onSelect,
}) {
  if (!x) {
    return (
      <aside aria-label="Action details" className="rounded-[14px] border min-h-[300px] flex flex-col items-center justify-center gap-2 p-8 text-center" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
        <span className="text-[15px] font-semibold">Select an action</span>
        <span className="text-[13px]" style={{ color: 'var(--ink-muted)' }}>What it will do, who asked for it and where it is now show here.</span>
      </aside>
    );
  }

  const st = STATUS[x.status] || STATUS.pending;
  const ex = explain(x);
  const res = resultOf(x);
  const hint = x.status === 'failed' ? failureHint(x, res) : '';
  const agent = agentLine(x);
  const mine = String(x.requested_by).toLowerCase() === String(me).toLowerCase();
  const selfBlocked = mine && othersCanConfirm;
  const isIso = x.action_type === 'isolate_host';
  const waitedMin = x.status === 'dispatched' ? minutesSince(x.dispatched_at) : null;
  const undoLabel = UNDO_LABEL[x.action_type];

  const why = [
    ['Asked by', isPlaybook(x) ? `${byOf(x)}${x.created_at ? `, ${stamp(x.created_at)}` : ''}` : `${nameOf(x.requested_by)}, ${stamp(x.created_at)}`],
    x.confirmed_by && ['Confirmed by', `${nameOf(x.confirmed_by)}, ${stamp(x.confirmed_at)}`],
    x.cancelled_by && x.status === 'cancelled' && ['Cancelled by', `${nameOf(x.cancelled_by)}${x.cancelled_at ? `, ${stamp(x.cancelled_at)}` : ''}`],
    x.case_id && ['Case', <Link key="c" to={`/cases/${x.case_id}`} style={{ color: 'var(--ink-link)' }}>{caseRef(x.case_id)}{x.case_title ? ` · ${x.case_title}` : ''}</Link>],
    x.reason && ['Reason given', x.reason],
    x.reverses_id && ['Reverses', <button key="r" type="button" onClick={() => onSelect(x.reverses_id)} className="text-left" style={{ color: 'var(--ink-link)' }}>the original action</button>],
    x.retry_of && ['Retry of', <button key="t" type="button" onClick={() => onSelect(x.retry_of)} className="text-left" style={{ color: 'var(--ink-link)' }}>the failed attempt</button>],
    isPlaybook(x) && ['Run', <Link key="p" to={`/playbooks?run=${x.triggered_by}`} style={{ color: 'var(--ink-link)' }}>Open {runRef(x.triggered_by)} in Playbooks</Link>],
  ].filter(Boolean);

  const ended = x.status === 'failed' ? 'Failed' : 'Done';
  const reached = { pending: 0, confirmed: 1, dispatched: 2, completed: 3, failed: 3, cancelled: x.confirmed_at ? 1 : 0 }[x.status] ?? 0;
  const steps = [
    ['Requested', stamp(x.created_at)],
    ['Confirmed', x.confirmed_at ? `${stamp(x.confirmed_at)} · ${String(nameOf(x.confirmed_by)).split(/[\s@]/)[0]}` : ''],
    ['Picked up', stamp(x.dispatched_at)],
    [ended, stamp(x.completed_at)],
  ];
  const barColor = (i) => {
    if (i < reached) return 'var(--ink-muted)';
    if (i > reached) return 'var(--surf-line)';
    return x.status === 'failed' ? 'var(--sev-crit-fg)' : x.status === 'cancelled' ? 'var(--sev-low-fg)' : x.status === 'completed' ? 'var(--sev-good-fg)' : 'var(--accent)';
  };

  const btnOutline = 'h-11 px-3.5 rounded-[10px] border text-sm font-medium disabled:opacity-50';
  const cancelForm = (
    <>
      <label className="flex flex-col gap-1.5 text-[13px] font-medium">Why cancel it? <span className="font-normal text-xs" style={{ color: 'var(--ink-faint)' }}>Saved on the action{x.case_id ? ' and its case' : ''}.</span>
        <textarea rows={2} autoFocus value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="e.g. Quarantining the file was enough" className="box-border px-[11px] py-[9px] rounded-[9px] border text-[13px] font-normal resize-none outline-none" style={{ background: 'var(--surf-bg)', borderColor: 'var(--accent)', color: 'var(--ink-text)' }} />
      </label>
      <div className="flex gap-2">
        <button type="button" disabled={busy || !cancelReason.trim()} onClick={onCancel} className="h-10 px-3.5 rounded-[9px] border text-[13.5px] font-medium disabled:opacity-50" style={secondaryBtnStyle}>{busy ? 'Cancelling…' : 'Cancel the action'}</button>
        <button type="button" onClick={() => setCancelling(false)} className="h-10 px-2.5 text-[13.5px]" style={{ color: 'var(--ink-muted)' }}>Keep it</button>
      </div>
    </>
  );
  const cancelBtn = canConfirm && <button type="button" onClick={() => setCancelling(true)} className={btnOutline} style={secondaryBtnStyle}>Cancel…</button>;

  let footer = null;
  if ((x.status === 'pending' || x.status === 'confirmed') && cancelling) {
    footer = cancelForm;
  } else if (x.status === 'pending') {
    if (!canConfirm) {
      footer = <span className="text-[13px]" style={{ color: 'var(--ink-muted)' }}>Waiting for someone who can confirm response actions.</span>;
    } else if (selfBlocked) {
      footer = <div className="flex flex-wrap items-center gap-2"><span className="flex-[1_1_200px] text-[13px]" style={{ color: 'var(--ink-muted)' }}>You asked for this, so someone else has to confirm it.</span>{cancelBtn}</div>;
    } else if (isIso) {
      footer = (
        <>
          <label className="flex flex-col gap-1.5 text-[13px]">Type <span className="font-mono">{x.hostname}</span> to isolate it
            <input type="text" value={typed} onChange={e => setTyped(e.target.value)} autoComplete="off" spellCheck={false} className="h-[42px] box-border px-3 rounded-[9px] border font-mono text-[13.5px] outline-none" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }} />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={onConfirm} disabled={busy || typed.trim() !== x.hostname || x.host_deleted} className="h-11 px-[18px] rounded-[10px] text-sm font-semibold disabled:opacity-45" style={{ background: 'var(--sev-crit-fg)', color: '#0A0A0A' }}>{busy ? 'Sending…' : `Isolate ${x.hostname}`}</button>
            {cancelBtn}
          </div>
        </>
      );
    } else {
      footer = (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={onConfirm} disabled={busy || x.host_deleted} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Sending…' : 'Confirm and send'}</button>
          {cancelBtn}
          <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>The agent picks it up within 30 s.</span>
        </div>
      );
    }
  } else if (x.status === 'confirmed') {
    footer = <div className="flex flex-wrap items-center gap-2"><span className="flex-[1_1_220px] text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>Sent. Waiting for the agent on {x.hostname} to pick it up. You can still cancel until it does.</span>{cancelBtn}</div>;
  } else if (x.status === 'dispatched') {
    footer = waitedMin != null && waitedMin >= GIVE_UP_MIN ? (
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex-[1_1_220px] text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>No result from the agent after {Math.floor(waitedMin)} min. It may or may not have run. If you stop waiting, a late result is still recorded.</span>
        {canConfirm && <button type="button" disabled={busy} onClick={onStopWaiting} className={btnOutline} style={secondaryBtnStyle}>Stop waiting</button>}
      </div>
    ) : <span className="text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>The agent on {x.hostname} picked this up {agoLong(x.dispatched_at)} and is running it. It can’t be called back now.</span>;
  } else if (x.status === 'completed') {
    footer = (
      <div className="flex flex-wrap items-center gap-2">
        {undoLabel && !x.undo_id && canCreate && !x.host_deleted && <button type="button" disabled={busy} onClick={onUndo} className={btnOutline} style={secondaryBtnStyle}>{undoLabel}</button>}
        {undoLabel && x.undo_id && <button type="button" onClick={() => onSelect(x.undo_id)} className="h-11 text-[13px]" style={{ color: 'var(--ink-link)' }}>{x.undo_status === 'completed' ? 'Undone · view' : `${undoLabel} queued · view`}</button>}
        {x.case_id && <Link to={`/cases/${x.case_id}`} className="text-[13px]" style={{ color: 'var(--ink-link)' }}>Open {caseRef(x.case_id)}</Link>}
      </div>
    );
  } else if (x.status === 'failed') {
    footer = (
      <div className="flex flex-wrap items-center gap-2">
        {canCreate && !x.host_deleted && <button type="button" disabled={busy} onClick={onRetry} className={primaryBtn} style={primaryBtnStyle}>Queue it again</button>}
        <Link to="/agents" className="text-[13px]" style={{ color: 'var(--ink-link)' }}>Agent Registry</Link>
      </div>
    );
  } else if (x.status === 'cancelled') {
    footer = <span className="text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>Cancelled by {nameOf(x.cancelled_by)}{x.cancel_reason ? `: “${x.cancel_reason}”` : '.'}</span>;
  }

  const undoNote = x.status === 'pending' || x.status === 'confirmed'
    ? (undoLabel ? `${undoLabel}, from this page once it’s done.` : x.action_type === 'kill_process' ? 'Can’t be undone. Restart the program if needed.' : '')
    : '';

  return (
    <aside aria-label="Action details" className="rounded-[14px] border flex flex-col min-w-0" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
      <div className="flex flex-col gap-2.5 pt-[18px] px-[22px] pb-3.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
        <span className={mono} style={{ color: 'var(--ink-link)' }}>ACTION · {String(TYPE_LABEL[x.action_type] || x.action_type).toUpperCase()}</span>
        <h2 className="m-0 font-heading text-xl font-semibold tracking-[-0.01em] leading-tight break-words">{titleOf(x)}</h2>
        <span className="inline-flex self-start items-center gap-[7px] h-6 px-[9px] rounded-md font-mono text-[10.5px] font-medium tracking-[0.06em]" style={{ background: st.chip }}><Marker st={x.status} size={7} />{st.l.toUpperCase()}</span>
      </div>

      <div className="flex flex-col gap-4 py-4 px-[22px]">
        {isIso && x.status === 'pending' && (
          <div className="flex flex-col gap-1.5 px-3.5 py-3 rounded-[10px]" style={{ background: 'var(--sev-crit-bg)' }}>
            <span className="text-[13.5px] font-semibold">This cuts {x.hostname} off the network</span>
            <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>
              All traffic stops except DNS and DragonEye, including connections already open.
              {x.is_internet_facing ? ` ${x.hostname} is internet-facing${x.criticality_score ? ` with importance ${x.criticality_score}` : ''}, so anything it serves goes down.` : ''}
              {' '}The host’s own firewall rules are kept, and Release isolation puts things back.
            </span>
          </div>
        )}

        <div className="flex flex-col gap-[7px]">
          <span className={mono} style={{ color: 'var(--ink-faint)' }}>WHAT HAPPENS</span>
          <span className="text-[13px] leading-relaxed" style={{ color: 'var(--ink-muted)' }}>{ex.what}</span>
          {ex.cmd && <code className="block px-3 py-2.5 rounded-[9px] border font-mono text-[11.5px] leading-[1.65] whitespace-pre-wrap break-all" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>{ex.cmd}</code>}
          {ex.note && <span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{ex.note}</span>}
        </div>

        {res && (
          <div className="flex flex-col gap-[7px]">
            <span className={mono} style={{ color: 'var(--ink-faint)' }}>WHAT THE AGENT REPORTED</span>
            <code className="block px-3 py-2.5 rounded-[9px] font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap break-all" style={{ background: x.status === 'failed' ? 'var(--sev-crit-bg)' : 'var(--sev-good-bg)' }}>
              {res.error || res.message || JSON.stringify(res)}{res.agent_version ? `\nagent ${res.agent_version}` : ''}
            </code>
            {hint && <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{hint}</span>}
          </div>
        )}

        <div className="flex flex-col gap-[5px]">
          <span className={`${mono} pb-0.5`} style={{ color: 'var(--ink-faint)' }}>WHY</span>
          {why.map(([k, v]) => (
            <span key={k} className="grid grid-cols-[98px_minmax(0,1fr)] gap-2.5 text-[12.5px] leading-relaxed"><span style={{ color: 'var(--ink-faint)' }}>{k}</span><span className="break-words" style={{ color: 'var(--ink-muted)' }}>{v}</span></span>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <span className={mono} style={{ color: 'var(--ink-faint)' }}>PROGRESS</span>
          <div className="grid grid-cols-4 gap-1">
            {steps.map(([l, t], i) => (
              <span key={l} className="flex flex-col gap-[5px] min-w-0">
                <span className="h-1 rounded-sm" style={{ background: barColor(i) }} />
                <span className="text-[11.5px]" style={{ fontWeight: i === reached ? 600 : 400, color: i <= reached ? 'var(--ink-text)' : 'var(--ink-faint)' }}>{l}</span>
                <span className="font-mono text-[10.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{t}</span>
              </span>
            ))}
          </div>
          <span className="flex items-start gap-2 text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}><span className="w-[7px] h-[7px] mt-1.5 shrink-0 rounded box-border border" style={{ background: agent.dot, borderColor: agent.dot === 'transparent' ? 'var(--ink-faint)' : agent.dot }} />{agent.t}</span>
        </div>

        {undoNote && <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-faint)' }}>To reverse it later: <span style={{ color: 'var(--ink-muted)' }}>{undoNote}</span></span>}
      </div>

      {(footer || error) && (
        <div className="mt-auto flex flex-col gap-2.5 py-3.5 px-[22px] border-t" style={{ borderColor: 'var(--surf-line)' }}>
          {error && <span role="alert" className="text-[12.5px]" style={{ color: 'var(--sev-crit-fg)' }}>{error}</span>}
          {footer}
        </div>
      )}
    </aside>
  );
}
